const DB_NAME = 'member-nest-ride-declarations';
const DB_VERSION = 1;
const PROOFS = 'proofs';
const CHECKERS = 'checkers';
const QUEUE = 'checkQueue';

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') return reject(new Error('Offline storage is unavailable on this device.'));
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(PROOFS)) db.createObjectStore(PROOFS, { keyPath: 'key' });
      if (!db.objectStoreNames.contains(CHECKERS)) db.createObjectStore(CHECKERS, { keyPath: 'key' });
      if (!db.objectStoreNames.contains(QUEUE)) db.createObjectStore(QUEUE, { keyPath: 'id' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function storeRequest(storeName, mode, run) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, mode); const store = tx.objectStore(storeName); const request = run(store);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    tx.oncomplete = () => db.close();
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

const scopedKey = (teamId, userId) => `${teamId}:${userId}`;

export async function cacheRideDeclarationOfflineProof({ teamId, userId, proof }) {
  await storeRequest(PROOFS, 'readwrite', store => store.put({ key: scopedKey(teamId, userId), ...proof, cachedAt: Date.now() }));
}

export async function readRideDeclarationOfflineProof({ teamId, userId }) {
  const value = await storeRequest(PROOFS, 'readonly', store => store.get(scopedKey(teamId, userId)));
  return value && Number(value.expiresAt || 0) > Date.now() ? value : null;
}

export async function cacheRideDeclarationCheckerPermit({ teamId, userId, permit }) {
  await storeRequest(CHECKERS, 'readwrite', store => store.put({ key: scopedKey(teamId, userId), ...permit, cachedAt: Date.now() }));
}

export async function readRideDeclarationCheckerPermit({ teamId, userId }) {
  const value = await storeRequest(CHECKERS, 'readonly', store => store.get(scopedKey(teamId, userId)));
  return value && Number(value.expiresAt || 0) > Date.now() ? value : null;
}

function newQueueId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}

export async function queueRideDeclarationOfflineCheck({ teamId, userId, credential, checkerPermit, checkedAt, memberName, versionNumber }) {
  const id = newQueueId();
  const row = { id, teamId, userId, credential, checkerPermit, checkedAt, memberName, versionNumber, queuedAt: Date.now() };
  await storeRequest(QUEUE, 'readwrite', store => store.put(row));
  return row;
}

export async function listRideDeclarationOfflineChecks({ teamId, userId }) {
  const rows = await storeRequest(QUEUE, 'readonly', store => store.getAll());
  return (rows || []).filter(row => row.teamId === teamId && row.userId === userId).sort((a, b) => a.queuedAt - b.queuedAt);
}

export async function removeRideDeclarationOfflineCheck(id) {
  await storeRequest(QUEUE, 'readwrite', store => store.delete(id));
}

function decodeBase64url(value) {
  const normalized = String(value || '').replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '='));
  return Uint8Array.from(binary, char => char.charCodeAt(0));
}

export function parseRideDeclarationOfflineCredential(raw) {
  const [prefix, payloadPart, signaturePart] = String(raw || '').trim().split('.');
  if (prefix !== 'MNRD1' || !payloadPart || !signaturePart) throw new Error('This is not a saved offline declaration proof.');
  let payload;
  try { payload = JSON.parse(new TextDecoder().decode(decodeBase64url(payloadPart))); }
  catch { throw new Error('This offline declaration proof is damaged.'); }
  return { payload, payloadPart, signature: decodeBase64url(signaturePart) };
}

export async function verifyRideDeclarationOfflineCredential(raw, verificationKey, { expectedType, teamId, at = Date.now() } = {}) {
  if (!window.crypto?.subtle) throw new Error('Offline proof verification is unavailable in this browser.');
  const parsed = parseRideDeclarationOfflineCredential(raw);
  const key = await window.crypto.subtle.importKey('jwk', verificationKey, { name: 'ECDSA', namedCurve: 'P-256' }, false, ['verify']);
  const valid = await window.crypto.subtle.verify({ name: 'ECDSA', hash: 'SHA-256' }, key, parsed.signature, new TextEncoder().encode(parsed.payloadPart));
  const payload = parsed.payload;
  if (!valid || payload?.v !== 1 || payload?.k !== verificationKey?.kid || payload?.y !== expectedType || payload?.t !== teamId) throw new Error('This offline declaration proof cannot be verified.');
  if (!Number.isFinite(Number(payload.iat)) || !Number.isFinite(Number(payload.exp)) || at < Number(payload.iat) - 300000 || at > Number(payload.exp)) throw new Error('This offline declaration proof has expired.');
  return payload;
}
